import React, { useEffect, useState, type FC } from 'react';
import { Button, Space, Textarea, Text } from '@g2u/ui-kit';
import { Helmet } from 'react-helmet-async';
import { observer } from 'mobx-react-lite';
import { useMutation } from 'react-query';
import { Form } from 'antd';

import { useServiceProductsUpdate, useServicePartsUpdate, useServicePrice, useServiceDiscountsUpdate } from '../hooks';
import { ServiceList, ServiceTotal, PartsList, DiscountList, LegacyServices } from '.';
import { putServiceAttributes } from 'queries';
import { Toast, parseErrors } from 'helpers';
import { type JsonDataItem } from 'types';
import { RootStore } from 'store';
import styles from '../edit.module.scss';
import { calculateTotalHours } from 'helpers/hooks/useTotalHours';
import { useWatch } from 'antd/es/form/Form';

export namespace ServiceDetails {
  enum JobSource {
    LEGACY = 'legacy',
    BMSV3 = 'bmsv3',
  }

  type Props = {
    isLocked: boolean;
  };

  export const $: FC<Props> = observer((props) => {
    const { isLocked } = props;
    const { job, setJob } = RootStore;
    const {
      updateServiceProducts,
      updateLegacyProducts,
      isLoading: isProductsUpdating,
      productUpdateQueue,
    } = useServiceProductsUpdate();
    const { updateServiceParts, isLoading: isPartsUpdating } = useServicePartsUpdate();
    const { updateServiceDiscounts, isLoading: isDiscountUpdating } = useServiceDiscountsUpdate();
    const { updateServicePrice, servicePrice } = useServicePrice();
    const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
    const [isJobUpdating, setIsJobUpdating] = useState<boolean>(false);
    const [form] = Form.useForm();
    const serviceDescription = job?.attributes?.find(
      (attribute: JsonDataItem) => attribute.key === 'technician_report',
    )?.value;
    const isLegacySource = job?.source === JobSource.LEGACY;
    const isLegacyEnabled = job?.support_legacy === true;
    const isClosed = ['cancelled', 'completed'].includes(job?.status);
    const products = useWatch('products', form);
    const totalHours = calculateTotalHours(products);
    const { mutate: updateDescription, isLoading: isAttributesLoading } = useMutation(putServiceAttributes, {
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
      onSuccess: (data: JsonDataItem) => {
        setJob(data);
      },
    });

    const isMutating = isDiscountUpdating || isPartsUpdating || isProductsUpdating || isAttributesLoading;

    const [productsAndPartsTriggered, setProductsAndPartsTriggered] = useState(false);
    const handleUpdateJobDetails = () => {
      const serviceId = job?.ID;

      if (isLegacyEnabled) {
        updateLegacyProducts({
          serviceId,
          initialData: { job: job?.job, consultations: job?.jobs_labour },
          newData: form.getFieldValue('legacy_products'),
        });
      } else {
        updateServiceProducts({
          serviceId,
          initialProducts: job?.products,
          newProducts: form.getFieldValue('products') || [],
          requestedAppointment: job?.attributes?.find(
            (attribute: JsonDataItem) => attribute.key === 'requested_appointment',
          )?.value,
        });
      }

      updateServiceParts({
        serviceId,
        initialParts: job?.service_parts,
        newParts: form.getFieldValue('parts'),
      });

      setProductsAndPartsTriggered(true);
    };

    useEffect(() => {
      const isDescriptionChanged = serviceDescription !== form.getFieldValue('description');
      const serviceId = job?.ID;

      const productsAndPartsComplete = !(isPartsUpdating || isProductsUpdating);

      if (productsAndPartsTriggered && productsAndPartsComplete && productUpdateQueue === 0) {
        setProductsAndPartsTriggered(false);
        updateServiceDiscounts({
          serviceId,
          newDiscounts: form.getFieldValue('discounts'),
        });
        isDescriptionChanged &&
          updateDescription({
            serviceId,
            attributes: [
              {
                field: 'technician_report',
                value: form.getFieldValue('description'),
              },
            ],
          });
      }
    }, [isPartsUpdating, isProductsUpdating, productsAndPartsTriggered]);

    const handleRecalculateCost = () => {
      setIsFormChanged(true);
      const formData = form.getFieldsValue(['legacy_products', 'products', 'parts', 'discounts']);
      updateServicePrice({ ...formData, job });
    };

    useEffect(() => {
      isMutating && setIsJobUpdating(true);
      if (!isMutating && isJobUpdating) {
        setIsJobUpdating(false);
        setIsFormChanged(false);
        Toast.show({ description: 'The service details have been updated', type: Toast.Type.Success });
      }
    }, [isMutating]);

    return (
      <>
        <Helmet>
          <title>Update Service Details</title>
        </Helmet>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
          <Form
            form={form}
            name="service-details-form"
            layout="horizontal"
            onValuesChange={() => setIsFormChanged(true)}
            onFinish={handleUpdateJobDetails}
            colon={false}
            preserve={true}
            disabled={isLocked}
          >
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
              <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
                {isLegacyEnabled ? (
                  // TODO: remove when legacy bms jobs are not supported
                  <LegacyServices.$
                    isLegacySource={isLegacySource}
                    job={job}
                    onSelect={(productValue) => {
                      form.setFieldValue('legacy_products', productValue);
                      handleRecalculateCost();
                    }}
                    form={form}
                    totalPrice={servicePrice.legacyProductsTotal}
                  />
                ) : (
                  <ServiceList.$
                    serviceId={job?.ID}
                    customerId={job?.customer?.ID}
                    isRequired={true}
                    onSelect={(productValue) => {
                      form.setFieldValue('products', productValue);
                      handleRecalculateCost();
                    }}
                    job={job}
                    form={form}
                    totalPrice={servicePrice.productsTotal}
                    onLoad={() => setIsFormChanged(false)}
                  />
                )}
              </Space.$>
              <PartsList.$
                isLocked={isLocked}
                isClosed={['cancelled', 'completed'].includes(job?.status)}
                serviceId={job?.ID}
                partsSelection={job?.service_parts?.map((item: JsonDataItem) => {
                  return {
                    id: item.ID,
                    qty: Number(item.qty).toFixed(2),
                    price: Number(item.total_price).toFixed(2),
                    description: item.description,
                    type: item.inventoryitem?.type,
                    details: item,
                  };
                })}
                onSelect={(partValue) => {
                  form.setFieldValue('parts', partValue);
                  handleRecalculateCost();
                }}
                totalPrice={servicePrice.partsTotal}
                onLoad={() => setIsFormChanged(false)}
              />
              <DiscountList.$
                onSelect={(discountValue) => {
                  form.setFieldValue('discounts', discountValue);
                  handleRecalculateCost();
                }}
                job={job}
                totalPrice={servicePrice.discountTotal}
                onLoad={() => setIsFormChanged(false)}
              />
              <ServiceTotal.$ data={servicePrice} totalHours={totalHours} />
              <Text.$ strong size={Text.Size.LG}>
                Service Description
              </Text.$>
              <Textarea.$
                disabled={isClosed || isLocked}
                initialValue={serviceDescription}
                className={styles.jobEdit__field}
                name={'description'}
                placeholder="Please describe what actions were taken to complete the service"
              />
            </Space.$>
          </Form>

          <Button.$
            type={Button.Type.Primary}
            dataTestId="update-service-details-btn"
            disabled={!isFormChanged || isLocked}
            onClick={() => {
              form.submit();
            }}
            style={{ float: 'right' }}
            loading={isJobUpdating}
          >
            Save
          </Button.$>
        </Space.$>
      </>
    );
  });
}
